import { useRef, useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { API, authHeaders } from "./api";

const SIZE_CLASS = {
  xs: "w-6 h-6 text-[9px]",
  sm: "w-8 h-8 text-[10px]",
  md: "w-10 h-10 text-[11px]",
  lg: "w-16 h-16 text-base",
  xl: "w-20 h-20 text-lg",
};

const getInitials = (name) => {
  if (!name) return "—";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Stable soft-tint palette drawn from the existing forest/gold aesthetic.
// Hashing the name keeps the same tint for a given person across the app.
const TINTS = [
  { bg: "#F1E8CF", fg: "#8a6a1f", ring: "#d4c58b" }, // gold
  { bg: "#E2EBE4", fg: "#0F3226", ring: "#B3C6B6" }, // forest
  { bg: "#F4E7D7", fg: "#7a5120", ring: "#E0C8A4" }, // tan
  { bg: "#EBE5D4", fg: "#5a4916", ring: "#C8BD93" }, // olive
  { bg: "#DDE6E1", fg: "#1b3e2f", ring: "#AEC2B4" }, // muted forest
];
const hashIdx = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h) % TINTS.length;
};

/**
 * Renders the <img> for a customer avatar, falling back to initials-on-tint.
 *
 * - Fetches the image via the authenticated /avatar endpoint (admin-only),
 *   so public pages never see it even if the URL leaks.
 * - Uses the `avatar_updated_at` timestamp as a cache-buster so a fresh
 *   upload shows instantly without a hard refresh.
 */
const AvatarCircle = ({
  customerId,
  applicant = 1,
  name,
  updatedAt,
  hasAvatar,
  size = "md",
  ringColor,
  onClick,
  testid,
  title,
  className = "",
}) => {
  const tint = useMemo(() => TINTS[hashIdx(name || customerId || "")], [name, customerId]);
  const [failed, setFailed] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const clickable = !!onClick;

  // Fetch the avatar via an authenticated XHR and turn it into a blob URL
  // so <img> can display it without leaking the admin token in the URL.
  // Re-fetches whenever the cache-busting `updatedAt` timestamp changes.
  useEffect(() => {
    let cancelled = false;
    let revoke = null;
    setFailed(false);
    if (!hasAvatar || !customerId) {
      setBlobUrl(null);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/customers/${customerId}/avatar?applicant=${applicant}`,
          { ...authHeaders(), responseType: "blob" },
        );
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [customerId, applicant, hasAvatar, updatedAt]);

  const sizeCls = SIZE_CLASS[size] || SIZE_CLASS.md;
  const ring = ringColor || "#fbf8f0"; // cream, matches the sections

  const content = hasAvatar && !failed && blobUrl ? (
    <img
      src={blobUrl}
      alt={name ? `${name} avatar` : "avatar"}
      className="w-full h-full object-cover"
      draggable={false}
    />
  ) : (
    <span
      className="w-full h-full flex items-center justify-center font-semibold tracking-wide select-none"
      style={{ color: tint.fg, background: tint.bg }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </span>
  );

  const ringStyle = { boxShadow: `0 0 0 2px ${ring}` };

  return (
    <span
      data-testid={testid}
      title={title || name || ""}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick(e);
        }
      }}
      className={`relative inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${sizeCls} ${
        clickable ? "cursor-pointer hover:opacity-90 transition-opacity" : ""
      } ${className}`}
      style={ringStyle}
    >
      {content}
    </span>
  );
};

/**
 * Compact avatar display used before the customer name across lists.
 * - Single customer → one circle.
 * - Joint customer   → two overlapping circles side by side.
 */
export const CustomerAvatar = ({ customer, size = "md", ringColor, testid, onClick }) => {
  const isJoint = customer?.applicant_type === "joint";
  const name1 = customer?.applicant_1?.full_name || customer?.name || "";
  const name2 = customer?.applicant_2?.full_name || customer?.joint_applicant_name || "";
  const upd1 = customer?.avatar_1_updated_at || null;
  const upd2 = customer?.avatar_2_updated_at || null;

  // Wrap so `onClick` always receives the applicant number that was clicked.
  const makeHandler = (applicant) =>
    onClick ? (e) => { e?.stopPropagation?.(); onClick(applicant, e); } : undefined;

  if (!isJoint) {
    return (
      <AvatarCircle
        customerId={customer.id}
        applicant={1}
        name={name1}
        hasAvatar={!!customer?.avatar_1_path}
        updatedAt={upd1}
        size={size}
        ringColor={ringColor}
        onClick={makeHandler(1)}
        testid={testid || "customer-avatar"}
      />
    );
  }

  // Joint — two circles, second overlapping the first by ~35%.
  const overlapMargin = {
    xs: "-ml-2",
    sm: "-ml-2.5",
    md: "-ml-3",
    lg: "-ml-5",
    xl: "-ml-6",
  }[size] || "-ml-3";

  return (
    <span
      data-testid={testid || "customer-avatar-joint"}
      className="relative inline-flex items-center shrink-0"
      style={{ marginRight: 2 }}
    >
      <AvatarCircle
        customerId={customer.id}
        applicant={1}
        name={name1}
        hasAvatar={!!customer?.avatar_1_path}
        updatedAt={upd1}
        size={size}
        ringColor={ringColor}
        onClick={makeHandler(1)}
        title={name1 ? `${name1} (Applicant 1)` : "Applicant 1"}
      />
      <span className={`${overlapMargin} relative z-0`}>
        <AvatarCircle
          customerId={customer.id}
          applicant={2}
          name={name2}
          hasAvatar={!!customer?.avatar_2_path}
          updatedAt={upd2}
          size={size}
          ringColor={ringColor}
          onClick={makeHandler(2)}
          title={name2 ? `${name2} (Applicant 2)` : "Applicant 2"}
        />
      </span>
    </span>
  );
};

/**
 * Editor used on the customer-edit page. One circle per applicant with
 * a hover overlay that reveals an "Add photo" / "Change photo" button and
 * a small remove button.
 */
export const CustomerAvatarEditor = ({ customer, applicant = 1, size = "lg", onChanged }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const name =
    applicant === 2
      ? customer?.applicant_2?.full_name || customer?.joint_applicant_name || "Applicant 2"
      : customer?.applicant_1?.full_name || customer?.name || "Applicant 1";
  const hasAvatar = !!customer?.[`avatar_${applicant}_path`];
  const updatedAt = customer?.[`avatar_${applicant}_updated_at`] || null;

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB)");
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    axios
      .post(`${API}/admin/customers/${customer.id}/avatar?applicant=${applicant}`, fd, authHeaders())
      .then(() => {
        toast.success(hasAvatar ? "Photo updated" : "Photo added");
        onChanged?.();
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Could not upload photo");
      })
      .finally(() => {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
      });
  };

  const onRemove = () => {
    if (!hasAvatar) return;
    if (!window.confirm(`Remove photo for ${name}?`)) return;
    setBusy(true);
    axios
      .delete(`${API}/admin/customers/${customer.id}/avatar?applicant=${applicant}`, authHeaders())
      .then(() => {
        toast.success("Photo removed");
        onChanged?.();
      })
      .catch(() => toast.error("Could not remove photo"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="inline-flex flex-col items-center gap-2" data-testid={`avatar-editor-${applicant}`}>
      <div className="relative group">
        <AvatarCircle
          customerId={customer.id}
          applicant={applicant}
          name={name}
          hasAvatar={hasAvatar}
          updatedAt={updatedAt}
          size={size}
          ringColor="#fbf8f0"
          onClick={() => fileRef.current?.click()}
          testid={`avatar-editor-circle-${applicant}`}
          title={hasAvatar ? `Change photo for ${name}` : `Add photo for ${name}`}
        />
        <span
          className="pointer-events-none absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-cream transition-opacity"
          aria-hidden="true"
        >
          {busy ? (
            <Loader2 size={16} strokeWidth={2} className="animate-spin" />
          ) : (
            <Camera size={16} strokeWidth={1.8} />
          )}
        </span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          data-testid={`avatar-file-input-${applicant}`}
        />
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`avatar-upload-btn-${applicant}`}
          className="font-semibold text-forest hover:text-gold-dark disabled:opacity-50"
        >
          {hasAvatar ? "Change photo" : "Add photo"}
        </button>
        {hasAvatar && (
          <>
            <span className="text-forest/20">·</span>
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              data-testid={`avatar-remove-btn-${applicant}`}
              className="inline-flex items-center gap-1 text-rose-600/80 hover:text-rose-700 disabled:opacity-50"
            >
              <Trash2 size={11} strokeWidth={1.8} />
              Remove
            </button>
          </>
        )}
      </div>
    </div>
  );
};


/**
 * WhatsApp-style full-screen lightbox for a customer avatar.
 *
 * Props:
 *   customer       — the customer record (must include id + name + applicant_type + avatar_*_path/updated_at)
 *   startApplicant — 1 or 2 (which applicant to open first, for joint customers)
 *   onClose        — callback when dismissed
 *   onChanged      — callback after a successful upload/remove so the parent reloads
 *
 * UX:
 *   - Dark backdrop, centred circular picture ~60-80vh.
 *   - Name shown under the picture.
 *   - For joint customers, left/right arrows swap applicants.
 *   - "Change photo" / "Remove photo" buttons for the currently shown applicant.
 *   - When there is no photo yet, shows the initials circle + an "Add photo" primary button.
 *   - Closes via ✕ button, Esc key, or clicking the dark backdrop.
 */
export const AvatarLightbox = ({ customer, startApplicant = 1, onClose, onChanged }) => {
  const isJoint = customer?.applicant_type === "joint";
  const [applicant, setApplicant] = useState(startApplicant === 2 ? 2 : 1);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const name =
    applicant === 2
      ? customer?.applicant_2?.full_name || customer?.joint_applicant_name || "Applicant 2"
      : customer?.applicant_1?.full_name || customer?.name || "Applicant 1";
  const otherName =
    applicant === 2
      ? customer?.applicant_1?.full_name || customer?.name || "Applicant 1"
      : customer?.applicant_2?.full_name || customer?.joint_applicant_name || "Applicant 2";
  const hasAvatar = !!customer?.[`avatar_${applicant}_path`];
  const updatedAt = customer?.[`avatar_${applicant}_updated_at`] || null;

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      if (isJoint && e.key === "ArrowLeft") setApplicant((a) => (a === 1 ? 2 : 1));
      if (isJoint && e.key === "ArrowRight") setApplicant((a) => (a === 1 ? 2 : 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isJoint, onClose]);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB)");
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    axios
      .post(`${API}/admin/customers/${customer.id}/avatar?applicant=${applicant}`, fd, authHeaders())
      .then(() => {
        toast.success(hasAvatar ? "Photo updated" : "Photo added");
        onChanged?.();
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Could not upload photo"))
      .finally(() => {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
      });
  };

  const onRemove = () => {
    if (!hasAvatar || busy) return;
    if (!window.confirm(`Remove photo for ${name}?`)) return;
    setBusy(true);
    axios
      .delete(`${API}/admin/customers/${customer.id}/avatar?applicant=${applicant}`, authHeaders())
      .then(() => {
        toast.success("Photo removed");
        onChanged?.();
      })
      .catch(() => toast.error("Could not remove photo"))
      .finally(() => setBusy(false));
  };

  const switchTo = (ap) => () => setApplicant(ap);

  return (
    <div
      data-testid="avatar-lightbox"
      data-lenis-prevent
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        data-testid="avatar-lightbox-close"
        aria-label="Close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-full bg-black/40 hover:bg-black/60 text-cream transition-colors"
      >
        <X size={20} strokeWidth={1.8} />
      </button>

      {/* Prev / next arrows for joint customers */}
      {isJoint && (
        <>
          <button
            type="button"
            onClick={switchTo(applicant === 1 ? 2 : 1)}
            data-testid="avatar-lightbox-prev"
            aria-label={`Show ${otherName}`}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/40 hover:bg-black/60 text-cream transition-colors"
          >
            <ChevronLeft size={22} strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={switchTo(applicant === 1 ? 2 : 1)}
            data-testid="avatar-lightbox-next"
            aria-label={`Show ${otherName}`}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/40 hover:bg-black/60 text-cream transition-colors"
          >
            <ChevronRight size={22} strokeWidth={1.8} />
          </button>
        </>
      )}

      {/* Centre column */}
      <div
        className="relative flex flex-col items-center gap-5 max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Large circle — AvatarCircle handles both image and initials. */}
        <div data-testid="avatar-lightbox-circle" className="pointer-events-none">
          <LargeAvatar
            customerId={customer.id}
            applicant={applicant}
            name={name}
            hasAvatar={hasAvatar}
            updatedAt={updatedAt}
          />
        </div>

        {/* Name under the picture */}
        <div className="text-center text-cream">
          <p
            data-testid="avatar-lightbox-name"
            className="font-serif text-xl sm:text-2xl leading-tight"
          >
            {name}
          </p>
          {isJoint && (
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-cream/60 mt-1">
              Applicant {applicant} of 2
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap justify-center">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="avatar-lightbox-upload"
            className="inline-flex items-center gap-2 bg-gold text-forest hover:bg-gold-soft px-4 py-2 rounded text-[11px] font-bold tracking-[0.18em] uppercase transition-colors disabled:opacity-50"
          >
            {busy ? (
              <Loader2 size={14} strokeWidth={2} className="animate-spin" />
            ) : (
              <Camera size={14} strokeWidth={2} />
            )}
            {hasAvatar ? "Change photo" : "Add photo"}
          </button>
          {hasAvatar && (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              data-testid="avatar-lightbox-remove"
              className="inline-flex items-center gap-2 bg-rose-600/90 hover:bg-rose-600 text-white px-4 py-2 rounded text-[11px] font-bold tracking-[0.18em] uppercase transition-colors disabled:opacity-50"
            >
              <Trash2 size={13} strokeWidth={2} />
              Remove photo
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPick}
            data-testid="avatar-lightbox-file-input"
          />
        </div>

        {/* Joint switch helper under the buttons */}
        {isJoint && (
          <div className="flex items-center gap-2 text-cream/70 text-[11px] tracking-[0.14em] uppercase font-bold">
            <button
              type="button"
              onClick={switchTo(1)}
              className={`transition-colors ${applicant === 1 ? "text-gold" : "hover:text-cream"}`}
              data-testid="avatar-lightbox-switch-1"
            >
              Applicant 1
            </button>
            <span className="text-cream/30">·</span>
            <button
              type="button"
              onClick={switchTo(2)}
              className={`transition-colors ${applicant === 2 ? "text-gold" : "hover:text-cream"}`}
              data-testid="avatar-lightbox-switch-2"
            >
              Applicant 2
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Large (~240-320px) centred circle used inside the lightbox. Fetches and
// renders the image via the same authenticated-blob pipeline as AvatarCircle,
// but at a bigger pixel size with a matching initials fallback.
const LargeAvatar = ({ customerId, applicant, name, hasAvatar, updatedAt }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const tint = useMemo(() => TINTS[hashIdx(name || customerId || "")], [name, customerId]);

  useEffect(() => {
    let cancelled = false;
    let revoke = null;
    setFailed(false);
    if (!hasAvatar || !customerId) { setBlobUrl(null); return; }
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/customers/${customerId}/avatar?applicant=${applicant}`,
          { ...authHeaders(), responseType: "blob" },
        );
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
      } catch { if (!cancelled) setFailed(true); }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [customerId, applicant, hasAvatar, updatedAt]);

  const sizeStyle = {
    width: "min(70vw, 70vh, 320px)",
    height: "min(70vw, 70vh, 320px)",
  };

  if (hasAvatar && !failed && blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={name ? `${name} avatar` : "avatar"}
        draggable={false}
        style={{ ...sizeStyle, objectFit: "cover" }}
        className="rounded-full shadow-2xl ring-4 ring-cream/20"
      />
    );
  }
  return (
    <div
      style={{ ...sizeStyle, color: tint.fg, background: tint.bg }}
      className="rounded-full shadow-2xl ring-4 ring-cream/20 flex items-center justify-center font-semibold select-none"
    >
      <span className="text-5xl sm:text-6xl tracking-widest">
        {getInitials(name)}
      </span>
    </div>
  );
};
